"use client";

import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { getOnlineStatusSnapshot } from "@/lib/shared/offline/online-store";
import { deleteIdbValue, offlineMutationIdbStore, readIdbValue, writeIdbValue } from "@/lib/shared/storage/idb";
const SCORE_MUTATION_QUEUE_KEY_PREFIX = "clutch:offline-score-mutations";
const MAX_SYNC_ATTEMPTS = 5;
function getOfflineScoreMutationStorageKey(userId?: string | null) {
  return `${SCORE_MUTATION_QUEUE_KEY_PREFIX}:${userId ?? "guest"}`;
}
type ScoreMutationStatus = "pending" | "syncing" | "failed";
type ScoreMutationMethod = "POST" | "PUT" | "PATCH";
export type OfflineScoreMutationRequest = {
  url: string;
  method?: ScoreMutationMethod;
  body: unknown;
  headers?: Record<string, string>;
  invalidateQueryKeys?: readonly QueryKey[];
};
export type OfflineScoreMutation = {
  id: string;
  idempotencyKey: string;
  createdAt: string;
  updatedAt: string;
  attemptCount: number;
  lastError: string | null;
  status: ScoreMutationStatus;
  request: {
    url: string;
    method: ScoreMutationMethod;
    body: unknown;
    headers: Record<string, string>;
    invalidateQueryKeys: readonly QueryKey[];
  };
};
export type OfflineScoreQueueState = {
  hydrated: boolean;
  isSyncing: boolean;
  lastSyncError: string | null;
  lastSyncedAt: string | null;
  items: OfflineScoreMutation[];
};
const EMPTY_QUEUE_STATE: OfflineScoreQueueState = {
  hydrated: false,
  isSyncing: false,
  lastSyncError: null,
  lastSyncedAt: null,
  items: []
};
let queueState: OfflineScoreQueueState = EMPTY_QUEUE_STATE;
const queueListeners = new Set<() => void>();
let queueHydrationPromise: Promise<OfflineScoreMutation[]> | null = null;
let queueSyncPromise: Promise<void> | null = null;
let queuePersistencePromise: Promise<void> = Promise.resolve();
let queueStorageKey = getOfflineScoreMutationStorageKey();
// Incremented on scope/clear changes so in-flight syncs can detect they're stale.
let scopeGeneration = 0;
function emitQueueState() {
  for (const listener of queueListeners) {
    listener();
  }
}
function setQueueState(updater: Partial<OfflineScoreQueueState> | ((current: OfflineScoreQueueState) => OfflineScoreQueueState)) {
  queueState = typeof updater === "function" ? updater(queueState) : {
    ...queueState,
    ...updater
  };
  emitQueueState();
}
async function persistQueueItems(updater: OfflineScoreMutation[] | ((currentItems: OfflineScoreMutation[]) => OfflineScoreMutation[])) {
  const nextItems = typeof updater === "function" ? updater(queueState.items) : updater;
  const persistenceKey = queueStorageKey;
  const persistenceGeneration = scopeGeneration;
  setQueueState(current => ({
    ...current,
    hydrated: true,
    items: nextItems
  }));
  queuePersistencePromise = queuePersistencePromise.then(async () => {
    if (persistenceGeneration !== scopeGeneration || persistenceKey !== queueStorageKey) {
      return;
    }
    await writeIdbValue(persistenceKey, nextItems, offlineMutationIdbStore);
  });
  await queuePersistencePromise;
  return nextItems;
}
export function setOfflineScoreQueueUserScope(userId?: string | null) {
  const nextStorageKey = getOfflineScoreMutationStorageKey(userId);
  if (nextStorageKey === queueStorageKey) {
    return;
  }
  scopeGeneration++;
  queueStorageKey = nextStorageKey;
  queueHydrationPromise = null;
  queueSyncPromise = null;
  queuePersistencePromise = Promise.resolve();
  queueState = EMPTY_QUEUE_STATE;
  lastQueueStateForCache = null;
  cachedNormalizedState = null;
  emitQueueState();
}
let cachedNormalizedState: (OfflineScoreQueueState & {
  pendingCount: number;
  failedCount: number;
}) | null = null;
let lastQueueStateForCache: OfflineScoreQueueState | null = null;
function getNormalizedQueueState() {
  if (lastQueueStateForCache === queueState && cachedNormalizedState) {
    return cachedNormalizedState;
  }
  lastQueueStateForCache = queueState;
  cachedNormalizedState = {
    ...queueState,
    pendingCount: queueState.items.filter(item => item.status === "pending").length,
    failedCount: queueState.items.filter(item => item.status === "failed").length
  };
  return cachedNormalizedState;
}
export function subscribeToOfflineScoreQueue(listener: () => void) {
  queueListeners.add(listener);
  return () => {
    queueListeners.delete(listener);
  };
}
const SERVER_QUEUE_SNAPSHOT = {
  ...EMPTY_QUEUE_STATE,
  pendingCount: 0,
  failedCount: 0
} as const;
export function useOfflineScoreQueueState() {
  return useSyncExternalStore(subscribeToOfflineScoreQueue, getNormalizedQueueState, _temp);
}
function _temp() {
  return SERVER_QUEUE_SNAPSHOT;
}
export async function hydrateOfflineScoreQueue() {
  if (!queueHydrationPromise) {
    queueHydrationPromise = readIdbValue<OfflineScoreMutation[]>(queueStorageKey, offlineMutationIdbStore).then(items => {
      const hydratedItems = items ?? [];
      setQueueState({
        hydrated: true,
        items: hydratedItems
      });
      return hydratedItems;
    });
  }
  return queueHydrationPromise;
}
function createMutationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `score-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
export function createScoreIdempotencyKey() {
  return createMutationId();
}
function createOfflineScoreMutation(request: OfflineScoreMutationRequest, idempotencyKey?: string): OfflineScoreMutation {
  const now = new Date().toISOString();
  const mutationId = createMutationId();
  return {
    id: mutationId,
    idempotencyKey: idempotencyKey ?? createScoreIdempotencyKey(),
    createdAt: now,
    updatedAt: now,
    attemptCount: 0,
    lastError: null,
    status: "pending",
    request: {
      url: request.url,
      method: request.method ?? "POST",
      body: request.body,
      headers: request.headers ?? {},
      invalidateQueryKeys: request.invalidateQueryKeys ?? []
    }
  };
}
export async function enqueueOfflineScoreMutation(request: OfflineScoreMutationRequest, idempotencyKey?: string) {
  await hydrateOfflineScoreQueue();
  const nextItem = createOfflineScoreMutation(request, idempotencyKey);
  await persistQueueItems(currentItems => [...currentItems, nextItem]);
  return nextItem;
}
export async function clearFailedOfflineScoreMutations() {
  await hydrateOfflineScoreQueue();
  await persistQueueItems(currentItems => currentItems.filter(item => item.status !== "failed"));
}
export async function clearOfflineScoreQueue() {
  scopeGeneration++;
  queueHydrationPromise = null;
  queueSyncPromise = null;
  queuePersistencePromise = Promise.resolve();
  queueState = {
    ...EMPTY_QUEUE_STATE,
    hydrated: true
  };
  emitQueueState();
  await deleteIdbValue(queueStorageKey, offlineMutationIdbStore);
}
class ScoreMutationHttpError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ScoreMutationHttpError";
    this.status = status;
  }
}
function isConnectivityError(error: unknown) {
  if (!getOnlineStatusSnapshot()) {
    return true;
  }
  if (error instanceof TypeError) {
    return true;
  }
  if (!(error instanceof Error)) {
    return false;
  }
  return /network|fetch|offline|connection/i.test(error.message);
}
function isRetryableHttpStatus(status: number) {
  return status === 408 || status === 425 || status === 429 || status >= 500;
}
export async function executeOfflineScoreRequest(request: OfflineScoreMutationRequest | OfflineScoreMutation["request"], idempotencyKey?: string) {
  const response = await fetch(request.url, {
    method: request.method ?? "POST",
    headers: {
      "Content-Type": "application/json",
      ...(idempotencyKey ? {
        "X-Idempotency-Key": idempotencyKey
      } : {}),
      ...(request.headers ?? {})
    },
    body: request.body === undefined ? undefined : JSON.stringify(request.body)
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new ScoreMutationHttpError(errorText || "Failed to sync offline score mutation", response.status);
  }
  if (response.status === 204) {
    return null;
  }
  const text = await response.text().catch(() => "");
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
async function invalidateQueuedScoreQueries(queryClient: QueryClient, queryKeys: readonly QueryKey[]) {
  for (const queryKey of queryKeys) {
    await queryClient.invalidateQueries({
      queryKey
    });
  }
}
function updateQueuedItem(items: OfflineScoreMutation[], itemId: string, updater: (item: OfflineScoreMutation) => OfflineScoreMutation) {
  return items.map(item => item.id === itemId ? updater(item) : item);
}
function buildRetryUpdater(itemId: string, message: string) {
  return (items: OfflineScoreMutation[]) => updateQueuedItem(items, itemId, current => {
    const attemptCount = current.attemptCount + 1;
    const status = attemptCount >= MAX_SYNC_ATTEMPTS ? "failed" : "pending";
    return {
      ...current,
      status,
      attemptCount,
      lastError: message,
      updatedAt: new Date().toISOString()
    };
  });
}
export async function syncQueuedScoreMutations(queryClient: QueryClient) {
  if (queueSyncPromise) {
    return queueSyncPromise;
  }
  const syncGeneration = scopeGeneration;
  queueSyncPromise = (async () => {
    await hydrateOfflineScoreQueue();
    if (!getOnlineStatusSnapshot() || syncGeneration !== scopeGeneration) {
      return;
    }
    setQueueState({
      isSyncing: true,
      lastSyncError: null
    });

    // Snapshot the IDs to process, but always read the latest items from
    // queueState before persisting so concurrent enqueues are not lost.
    const idsToSync = queueState.items.filter(item => item.status !== "failed").map(item => item.id);
    for (const itemId of idsToSync) {
      if (syncGeneration !== scopeGeneration) {
        return;
      }
      const item = queueState.items.find(i => i.id === itemId);
      if (!item || item.status === "failed") {
        continue;
      }
      await persistQueueItems(currentItems => updateQueuedItem(currentItems, item.id, current => ({
        ...current,
        status: "syncing",
        updatedAt: new Date().toISOString()
      })));
      try {
        await executeOfflineScoreRequest(item.request, item.idempotencyKey);
        if (syncGeneration !== scopeGeneration) {
          return;
        }
        await persistQueueItems(currentItems => currentItems.filter(current => current.id !== item.id));
        await invalidateQueuedScoreQueries(queryClient, item.request.invalidateQueryKeys);
      } catch (error) {
        if (syncGeneration !== scopeGeneration) {
          return;
        }
        if (isConnectivityError(error)) {
          const message = error instanceof Error ? error.message : "Network unavailable";
          await persistQueueItems(buildRetryUpdater(item.id, message));
          setQueueState({
            isSyncing: false,
            lastSyncError: message
          });
          return;
        }
        if (error instanceof ScoreMutationHttpError && isRetryableHttpStatus(error.status)) {
          await persistQueueItems(buildRetryUpdater(item.id, error.message));
          setQueueState({
            isSyncing: false,
            lastSyncError: error.message
          });
          return;
        }
        await persistQueueItems(currentItems => updateQueuedItem(currentItems, item.id, current => ({
          ...current,
          status: "failed",
          attemptCount: current.attemptCount + 1,
          lastError: error instanceof Error ? error.message : "Score sync failed",
          updatedAt: new Date().toISOString()
        })));
        setQueueState({
          lastSyncError: error instanceof Error ? error.message : "Score sync failed"
        });
      }
    }
    if (syncGeneration !== scopeGeneration) {
      return;
    }
    setQueueState({
      isSyncing: false,
      lastSyncError: null,
      lastSyncedAt: new Date().toISOString()
    });
  })().finally(() => {
    queueSyncPromise = null;
    if (syncGeneration === scopeGeneration) {
      setQueueState(current => ({
        ...current,
        isSyncing: false
      }));
    }
  });
  return queueSyncPromise;
}
export function getOfflineScoreQueueSnapshot() {
  return getNormalizedQueueState();
}
export function getOfflineScoreQueuePendingCount() {
  return getNormalizedQueueState().pendingCount;
}
export function getOfflineScoreQueueFailedCount() {
  return getNormalizedQueueState().failedCount;
}